import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

export const PremiumDialog = ({ open, onClose }) => {
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState("loop_premium_monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/premium/status").then(setStatus).catch(() => {});
  }, [open]);

  if (!open) return null;

  const buy = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await api.post("/payments/checkout", {
        lookup_key: plan,
        origin_url: window.location.origin,
      });
      track("checkout_started", { plan });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  const daily = status?.daily;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="premium-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative glass w-full max-w-lg p-10 rise max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="close-premium-dialog"
          aria-label="Close"
          className="absolute top-5 right-5 p-2 text-muted-foreground hover:text-white transition-colors duration-200"
        >
          <X className="h-4 w-4" />
        </button>

        <span className="overline">Loop Pro</span>
        <h2 className="mt-4 font-display text-3xl tracking-tight leading-none">
          {status?.premium ? "You're on Loop Pro" : "Unlock every possibility"}
        </h2>

        {!status?.premium && (
          <div className="mt-7 grid grid-cols-2 gap-3">
            {(status?.plans || []).map((p) => {
              const on = plan === p.lookup_key;
              return (
                <button
                  key={p.lookup_key}
                  onClick={() => setPlan(p.lookup_key)}
                  data-testid={`plan-${p.interval}`}
                  aria-pressed={on}
                  className={`p-5 text-left border transition-colors duration-200 ${
                    on ? "border-primary/60 bg-primary/10" : "border-white/10 hover:bg-white/[0.05]"
                  }`}
                >
                  <p className="font-display text-xl">{p.label.split(" / ")[0]}</p>
                  <p className="overline mt-2">per {p.interval}</p>
                </button>
              );
            })}
          </div>
        )}

        <ul className="mt-8 space-y-3">
          {(status?.perks || []).map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={2} />
              {p}
            </li>
          ))}
        </ul>

        {!status?.premium && daily && (
          <p className="mt-7 text-xs text-muted-foreground" data-testid="free-usage-note">
            Free today: {daily.interview_messages_used}/{daily.interview_messages_limit} AI replies used ·{" "}
            {daily.path_generations_used}/{daily.path_generations_limit} possibility generations ·{" "}
            {status.roadmaps_used}/{status.roadmap_limit} roadmaps. Limits reset every 24 hours.
          </p>
        )}

        {status?.premium ? (
          <p className="mt-9 text-sm text-muted-foreground" data-testid="premium-active-note">
            Everything is unlocked — no daily caps. Thanks for backing Loop.
          </p>
        ) : (
          <button
            onClick={buy}
            disabled={busy}
            data-testid="buy-premium-button"
            className="mt-8 w-full flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-40 hover:bg-primary/90 hover:-translate-y-0.5 transition-[transform,background-color,opacity] duration-200"
          >
            <Sparkles className="h-4 w-4" />
            {busy
              ? "Opening checkout…"
              : `Get Loop Pro — ${plan === "loop_premium_yearly" ? "$79/yr" : "$9.99/mo"}`}
          </button>
        )}
        <p className="mt-4 text-xs text-muted-foreground">
          Secure checkout by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
};
